import React from 'react';
import {
  Banner,
  Button,
  ButtonGroup,
  Modal,
  PrimaryButton,
} from '@common/components';
import { useMutation } from '@common/hooks';
import { ArchiveTierMutation } from '@institutions/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Space } from 'antd';
import { tierHumanizeVisibilityOffset } from '@institutions/services';

type SuppliersManagementArchiveTierModalProps = {
  tier: { id: string };
  defaultTier: { name: string; fee: number; visibilityOffset: number };
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onArchive: () => void;
};

export function SuppliersManagementArchiveTierModal({
  tier,
  defaultTier,
  visible,
  setVisible,
  onArchive,
}: SuppliersManagementArchiveTierModalProps) {
  const [mutation, { loading }] = useMutation(ArchiveTierMutation, {
    onCompleted: (data) => {
      if (data.archiveTier?.tier) {
        onArchive();
        createMessage(
          'success',
          `${data.archiveTier.tier.name} successfully removed`
        );
        setVisible(false);
      } else if (data.archiveTier?.errors) {
        createMessage('error', data.archiveTier.errors[0].message);
      }
    },
    onError: () => {
      createMessage('error', 'Failed to perform action. Please try again.');
    },
    refetchQueries: ['Suppliers'],
  });

  const onOk = () => {
    mutation({ variables: { tierId: tier.id } });
  };

  const onCancel = () => {
    setVisible(false);
  };

  const Footer = (
    <ButtonGroup align="right">
      <Button key="cancel" size="md" onClick={onCancel} loading={loading}>
        Cancel
      </Button>
      <PrimaryButton key="submit" onClick={onOk} loading={loading}>
        Remove Tier
      </PrimaryButton>
    </ButtonGroup>
  );

  return (
    <>
      <Modal
        title="Confirm removing tier?"
        visible={visible}
        onCancel={onCancel}
        footer={Footer}
        className="suppliers-management-archive-tier-modal"
      >
        <Space direction="vertical" size="middle">
          Suppliers listed in this tier will be moved to:
          <Banner type="neutral" showIcon={false}>
            <strong>{defaultTier.name}</strong>, Delay{' '}
            {tierHumanizeVisibilityOffset(defaultTier.visibilityOffset)}, Admin
            Fee {defaultTier.fee}%
          </Banner>
        </Space>
      </Modal>
      <style jsx>{`
        :global(.suppliers-management-archive-tier-modal .ant-modal-body) {
          padding-bottom: 16px;
          padding-top: 16px;
        }
      `}</style>
    </>
  );
}
